<template>
  <div class="prices-page container-fluid py-4">
    <h1 class="mb-3">Product Pricing</h1>

    <BAlert show variant="info" class="mb-4">
      <strong>Heads up:</strong> Changes here apply to
      <strong>new subscriptions only</strong>. Existing subscribers keep their
      current price until they re-subscribe or change tier.
    </BAlert>

    <div v-if="isBusy" class="text-center py-5">
      <BSpinner />
    </div>

    <div v-else>
      <section
        v-for="product in productGroups"
        :key="product.productId"
        class="product-pricing-group mb-5"
      >
        <h2 class="h4">{{ productTitle(product.productName) }}</h2>
        <div
          v-for="group in product.groups"
          :key="`${product.productId}-${group.key}`"
          class="pricing-group mb-4"
        >
          <h3 class="h5">{{ group.title }}</h3>
          <BTable
            :items="group.rows"
            :fields="group.fields || fields"
            striped
            small
            hover
          >
            <template #cell(amount)="{ item }">
              <div class="d-flex align-items-center">
                <span class="mr-1 text-muted">$</span>
                <BFormInput
                  :value="currentEdit(item.id, item.amount)"
                  type="text"
                  inputmode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  size="sm"
                  style="max-width: 120px"
                  :disabled="savingPriceId === item.id"
                  @input="v => onAmountInput(item.id, v)"
                />
                <small class="text-muted ml-2"
                  >(current: {{ formatAmount(item.amount) }})</small
                >
              </div>
            </template>
            <template #cell(stripe)="{ item }">
              <div
                class="small"
                style="line-height: 1.3; font-family: monospace; font-size: 11px"
              >
                <div class="text-muted">{{ item.lookup_key }}</div>
                <a
                  v-if="item.stripe_price_id"
                  :href="stripeDashboardUrl(item.stripe_price_id)"
                  target="_blank"
                  rel="noopener"
                  :title="`Open ${item.stripe_price_id} in Stripe dashboard`"
                >
                  {{ item.stripe_price_id }}
                  <i
                    class="fas fa-external-link-alt ml-1"
                    style="font-size: 9px"
                  />
                </a>
              </div>
            </template>
            <template #cell(updated_at)="{ item }">
              {{ formatDate(item.updated_at) }}
            </template>
            <template #cell(actions)="{ item }">
              <BButton
                size="sm"
                variant="primary"
                class="mr-1"
                :disabled="!isDirty(item) || savingPriceId === item.id"
                @click="requestSave(item)"
              >
                <BSpinner small v-if="savingPriceId === item.id" />
                <span v-else>Save</span>
              </BButton>
              <BButton
                size="sm"
                variant="outline-secondary"
                @click="openHistory(item)"
                :title="`${item.history ? item.history.length : 0} changes`"
              >
                <i class="fas fa-history" />
              </BButton>
            </template>
          </BTable>
        </div>
      </section>
    </div>

    <ConfirmPriceChangeModal ref="confirmModal" @confirm="performSave" />
    <PriceHistoryModal ref="historyModal" />
  </div>
</template>

<script>
import {
  BTable,
  BSpinner,
  BAlert,
  BFormInput,
  BButton
} from "bootstrap-vue";
import ConfirmPriceChangeModal from "@/components/Prices/ConfirmPriceChangeModal.vue";
import PriceHistoryModal from "@/components/Prices/PriceHistoryModal.vue";

export default {
  name: "Prices",
  components: {
    BTable,
    BSpinner,
    BAlert,
    BFormInput,
    BButton,
    ConfirmPriceChangeModal,
    PriceHistoryModal
  },
  data() {
    return {
      prices: [],
      isBusy: false,
      savingPriceId: null,
      stripeMode: "live",
      edits: {},
      fields: [
        { key: "team_size.label.en", label: "Team size" },
        { key: "stripe_interval", label: "Interval" },
        { key: "amount", label: "Amount (CAD)" },
        { key: "stripe", label: "Stripe" },
        { key: "updated_at", label: "Last change" },
        { key: "actions", label: "" }
      ],
      // Featured slot packs have no team size: the tier is their first column.
      tierFields: [
        { key: "tier", label: "Slots" },
        { key: "stripe_interval", label: "Interval" },
        { key: "amount", label: "Amount (CAD)" },
        { key: "stripe", label: "Stripe" },
        { key: "updated_at", label: "Last change" },
        { key: "actions", label: "" }
      ]
    };
  },
  computed: {
    // Outer grouping: one section per product (diffusion, ai, ...); inner
    // grouping unchanged (agency x interval), same table markup per group.
    productGroups() {
      const byProduct = {};
      this.prices.forEach(p => {
        const productId = p.product ? p.product.id : p.product_id;
        const productName = p.product ? p.product.name : String(p.product_id);
        if (!byProduct[productId]) {
          byProduct[productId] = { productId, productName, rows: [] };
        }
        byProduct[productId].rows.push(p);
      });
      return Object.values(byProduct)
        .sort((a, b) => a.productId - b.productId)
        .map(product => ({
          ...product,
          groups: this.groupPricingRows(product.rows)
        }));
    }
  },
  created() {
    this.fetchPrices();
  },
  methods: {
    groupPricingRows(rows) {
      if (rows.some(p => p.tier !== null && p.tier !== undefined)) {
        return this.groupTierRows(rows);
      }
      const groups = {
        non_agency_month: {
          key: "non_agency_month",
          title: "Non-agency — Monthly",
          rows: []
        },
        non_agency_year: {
          key: "non_agency_year",
          title: "Non-agency — Yearly",
          rows: []
        },
        agency_month: {
          key: "agency_month",
          title: "Agency — Monthly",
          rows: []
        },
        agency_year: { key: "agency_year", title: "Agency — Yearly", rows: [] }
      };
      rows.forEach(p => {
        const prefix = p.is_agency ? "agency" : "non_agency";
        const suffix = p.stripe_interval === "year" ? "year" : "month";
        groups[`${prefix}_${suffix}`].rows.push(p);
      });
      return Object.values(groups).filter(g => g.rows.length > 0);
    },
    // Tier products (featured slots, D2): no agency split, rows sorted by tier.
    groupTierRows(rows) {
      const byTier = (a, b) => a.tier - b.tier;
      return [
        { key: "tier_month", title: "Monthly", interval: "month" },
        { key: "tier_year", title: "Yearly", interval: "year" }
      ]
        .map(group => ({
          key: group.key,
          title: group.title,
          fields: this.tierFields,
          rows: rows
            .filter(p =>
              group.interval === "year"
                ? p.stripe_interval === "year"
                : p.stripe_interval !== "year"
            )
            .sort(byTier)
        }))
        .filter(g => g.rows.length > 0);
    },
    // Product name lands raw from the API (e.g. "ai", "diffusion") — display-case it.
    productTitle(name) {
      if (!name) return "";
      if (name.length <= 3) return name.toUpperCase();
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    fetchPrices() {
      this.isBusy = true;
      this.$http
        .get("/enroll_admin/prices")
        .then(({ data }) => {
          this.prices = data.prices;
          this.stripeMode = data.stripe_mode || "live";
          this.edits = {};
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    stripeDashboardUrl(stripePriceId) {
      const prefix =
        this.stripeMode === "test"
          ? "https://dashboard.stripe.com/test/prices/"
          : "https://dashboard.stripe.com/prices/";
      return `${prefix}${stripePriceId}`;
    },
    currentEdit(priceId, currentCents) {
      if (this.edits[priceId] !== undefined) return this.edits[priceId];
      return (currentCents / 100).toFixed(2);
    },
    onAmountInput(priceId, value) {
      this.$set(this.edits, priceId, value);
    },
    isDirty(item) {
      const raw = this.edits[item.id];
      if (raw === undefined || raw === "") return false;
      const cents = Math.round(parseFloat(raw) * 100);
      return Number.isFinite(cents) && cents > 0 && cents !== item.amount;
    },
    rowLabel(item) {
      if (item.tier !== null && item.tier !== undefined) {
        const product = item.product ? item.product.name : "product";
        return `${product} ${item.tier} slots ${item.stripe_interval}ly`;
      }
      const teamLabel =
        (item.team_size && item.team_size.label && item.team_size.label.en) ||
        item.team_size_id;
      const agency = item.is_agency ? " (agency)" : "";
      return `team size ${teamLabel} ${item.stripe_interval}ly${agency}`;
    },
    openHistory(item) {
      this.$refs.historyModal.open({
        title: `History — ${this.rowLabel(item)}`,
        entries: item.history || []
      });
    },
    requestSave(item) {
      const raw = this.edits[item.id];
      const newCents = Math.round(parseFloat(raw) * 100);
      this.$refs.confirmModal.open({
        priceId: item.id,
        label: this.rowLabel(item),
        oldAmount: item.amount,
        newAmount: newCents
      });
    },
    performSave({ priceId, newAmount }) {
      this.savingPriceId = priceId;
      this.$http
        .put(`/enroll_admin/prices/${priceId}`, { amount: newAmount })
        .then(({ data }) => {
          const idx = this.prices.findIndex(p => p.id === priceId);
          if (idx !== -1) {
            const existingHistory = this.prices[idx].history || [];
            const updatedHistory = data.history
              ? [data.history, ...existingHistory]
              : existingHistory;
            this.$set(this.prices, idx, {
              ...this.prices[idx],
              ...data.price,
              history: updatedHistory
            });
          }
          this.$delete(this.edits, priceId);
          this.$bvToast.toast("Saved. New price active for future signups.", {
            title: "Price updated",
            variant: "success"
          });
        })
        .catch(err => {
          const msg = this.extractErrorMessage(err);
          this.$bvToast.toast(msg, { title: "Error", variant: "danger" });
        })
        .finally(() => {
          this.savingPriceId = null;
        });
    },
    extractErrorMessage(err) {
      const data = err.response && err.response.data;
      if (!data) return "Save failed. Please retry.";
      if (typeof data.error === "string") return data.error;
      // Lumen validation errors come back as { field: [messages] }
      const firstField = Object.keys(data).find(k => Array.isArray(data[k]));
      if (firstField && data[firstField].length > 0) return data[firstField][0];
      return "Save failed. Please retry.";
    },
    formatAmount(cents) {
      return new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD"
      }).format(cents / 100);
    },
    formatDate(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString("en-CA");
    }
  }
};
</script>
